<template>
    <Card pageTitle="Cost Adjustments">
        <AdjustmentTabs />

        <div class="history-heading">
            <div>
                <strong>Cost Adjustments</strong>
                <p>Completed unit-cost changes recorded when purchase orders are posted to inventory.</p>
            </div>
            <span class="automatic-label"><i class="fas fa-sync-alt"></i> Automatic from receiving</span>
        </div>

        <form class="history-search" @submit.prevent="searchAdjustments">
            <div>
                <label for="cost-adjustment-search">Search by adjustment ID, purchase order, or supplier</label>
                <CustomInput id="cost-adjustment-search" small type="text" v-model="search" />
            </div>
            <Button :condensed="true" noMargin>Search</Button>
        </form>

        <div v-if="loading" class="loading-container">
            <div class="spinner-border text-primary" role="status"><span class="sr-only">Loading...</span></div>
            <p>Loading cost adjustments...</p>
        </div>

        <AllowPermission v-else :permission="PERMISSION_CODES.CMS_VIEW_ADJUSTMENT">
            <div class="table-responsive history-table-wrap">
                <table class="history-table">
                    <thead>
                        <tr>
                            <th>Adjustment ID</th>
                            <th>Source</th>
                            <th>Supplier</th>
                            <th>Location</th>
                            <th class="numeric">Items</th>
                            <th>Completed</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="adjustment in adjustments" :key="adjustment.id">
                            <td>
                                <LinkComponent :to="`/inventory-adjustments/cost/${adjustment.name}`" class="adjustment-link">
                                    <span>{{ adjustment.name }}</span>
                                    <small>Cost adjustment</small>
                                </LinkComponent>
                            </td>
                            <td>
                                <span class="source-value">{{ adjustment.source_name || "Purchase order" }}</span>
                                <small>{{ sourceLabel(adjustment.source_type) }}</small>
                            </td>
                            <td>{{ adjustment.supplier_name || "—" }}</td>
                            <td>{{ adjustment.warehouse_name || "—" }}</td>
                            <td class="numeric">{{ adjustment.totalItems }}</td>
                            <td>
                                <span>{{ formatDate(adjustment.createdAt) }}</span>
                                <small>{{ formatTime(adjustment.createdAt) }}</small>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div v-if="!adjustments.length" class="empty-state">
                <i class="fas fa-file-invoice-dollar"></i>
                <h4>No cost adjustments found</h4>
                <p>Initial product costs are not adjustments. A record will appear when a later received cost changes.</p>
            </div>

            <Paginator v-if="totalPages > 1" @change="updatePage" :page="page" :totalPages="totalPages" />
        </AllowPermission>
    </Card>
</template>

<script>
import AdjustmentTabs from "../../../components/inventory/AdjustmentTabs.vue"
import AllowPermission from "../../../components/layout/AllowPermission.vue"
import Paginator from "../../../components/other/Paginator.vue"
import CustomInput from "../../../components/crud/custom-input.vue"
import Button from "../../../components/UI/Button.vue"
import Card from "../../../components/UI/Card.vue"
import LinkComponent from "../../../components/UI/LinkComponent.vue"
import { PERMISSION_CODES } from "../../../store/permission"
import CostFetchApi from "../../../utils/fetch/cost-fetch-api"

export default {
    components: { AdjustmentTabs, AllowPermission, Paginator, CustomInput, Button, Card, LinkComponent },
    data() {
        return {
            search: "",
            page: 1,
            limit: 10,
            totalPages: 0,
            loading: false,
            adjustments: [],
            PERMISSION_CODES,
        }
    },
    async mounted() {
        await this.fetchAdjustments()
    },
    methods: {
        async fetchAdjustments() {
            this.loading = true
            try {
                const data = await CostFetchApi.getCostAdjustments({
                    page: this.page,
                    limit: this.limit,
                    search: this.search,
                })
                this.adjustments = data.result || []
                this.totalPages = data.totalPages || 0
            } catch (err) {
                console.error("Error fetching cost adjustments", err)
                this.$toast.error("Failed to fetch cost adjustments")
            } finally {
                this.loading = false
            }
        },
        searchAdjustments() {
            this.page = 1
            this.fetchAdjustments()
        },
        updatePage(page) {
            if (page > 0 && page <= this.totalPages) {
                this.page = page
                this.fetchAdjustments()
            }
        },
        sourceLabel(sourceType) {
            return sourceType === "PURCHASE_ORDER" ? "Purchase order" : sourceType || "Inventory"
        },
        formatDate(value) {
            if (!value) return "—"
            return new Date(value).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })
        },
        formatTime(value) {
            if (!value) return ""
            return new Date(value).toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit" })
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../../assets/variables.scss";

.history-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: $sectionGap;
    margin-bottom: $sectionGap;
}

.history-heading strong { display: block; margin-bottom: 5px; font-size: 1.08rem; }
.history-heading p { margin: 0; color: rgba($textColor, 0.66); }

.automatic-label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex: 0 0 auto;
    padding: 7px 11px;
    border-radius: 999px;
    color: $primaryColor;
    background: rgba($primaryColor, 0.08);
    font-size: 0.82rem;
    font-weight: 600;
}

.history-search {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    max-width: 610px;
    margin-bottom: $sectionGap * 1.5;
}

.history-search > div { flex: 1; }
.history-search label { display: block; margin-bottom: 6px; color: rgba($textColor, 0.72); font-size: 0.84rem; font-weight: 600; }

.history-table-wrap { border: 1px solid $lightBorderColor; border-radius: $cardRadius; }
.history-table { width: 100%; min-width: 830px; border-collapse: collapse; }
.history-table th { padding: 13px 15px; border-bottom: 1px solid $lightBorderColor; color: rgba($textColor, 0.68); background: rgba($primaryColor, 0.035); font-size: 0.78rem; letter-spacing: 0.025em; text-align: left; text-transform: uppercase; }
.history-table td { padding: 14px 15px; border-bottom: 1px solid rgba($lightBorderColor, 0.75); vertical-align: middle; }
.history-table tbody tr:last-child td { border-bottom: 0; }
.history-table tbody tr:hover { background: rgba($primaryColor, 0.025); }
.history-table small { display: block; margin-top: 3px; color: rgba($textColor, 0.54); }
.numeric { text-align: right !important; }
.adjustment-link { display: inline-flex; flex-direction: column; color: $primaryColor; font-weight: 600; }
.adjustment-link:hover { text-decoration: none; }
.source-value { font-weight: 600; }

.loading-container,
.empty-state { padding: 48px 20px; text-align: center; color: rgba($textColor, 0.65); }
.loading-container p { margin: 10px 0 0; }
.empty-state i { margin-bottom: 14px; color: rgba($primaryColor, 0.38); font-size: 2rem; }
.empty-state h4 { margin-bottom: 7px; color: $textColor; }
.empty-state p { max-width: 520px; margin: 0 auto; }

@media (max-width: 720px) {
    .history-heading { flex-direction: column; }
    .history-search { align-items: stretch; flex-direction: column; }
}
</style>
